/// <amd-module name="Wit/Common/SprintEditorDialog/SprintEditorDialog" />

import { ITeamSettings } from "Agile/Contracts/Common";
import * as React from "react";
import { TeamContext, WebApiTeam, WebApiTeamRef } from "Tfs/Platform/Contracts/Core";
import { getTFSPageData } from "Tfs/Platform/Page";
import { ICoreRestClient } from "Tfs/Platform/RestClient/Core";
import { ITfsTeamService } from "TFS/Services/TfsTeamService";
import { IObservableValue, ObservableArray, ObservableValue } from "VSS/Core/Observable";
import { format } from "VSS/Core/Util/String";
import { IVssHistoryService } from "VSS/Platform/History";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { ISettingsService, SettingsUserScope } from "VSS/Platform/Settings";
import { Button } from "VSSUI/Button";
import { DatePicker } from "VSSUI/DatePicker";
import { Dialog } from "VSSUI/Dialog";
import { EditableDropdown } from "VSSUI/EditableDropdown";
import { FormItem } from "VSSUI/FormItem";
import { IconSize } from "VSSUI/Icon";
import { IListBoxItem } from "VSSUI/ListBox";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { Observer } from "VSSUI/Observer";
import { TextField } from "VSSUI/TextField";
import { DropdownSelection } from "VSSUI/Utilities/DropdownSelection";
import { TreeNodeStructureType, TreeStructureGroup, WorkItemClassificationNode } from "Wit/Clients/Wit/Contracts/WorkItemTracking";
import { getWorkItemTrackingClient } from "Wit/Clients/Wit/RestClient/WorkItemTracking";
import { IWorkItemTrackingRestClient } from "Wit/Clients/Wit/RestClient/WorkItemTracking.Types";
import { TeamSetting } from "Wit/Clients/Work/Contracts/Work";
import { getWorkClient } from "Wit/Clients/Work/RestClient/Work";
import { IWorkRestClient } from "Wit/Clients/Work/RestClient/Work.Types";
import { shiftToUTC } from "Wit/Common/DateUtils";
import * as Resources from "Wit/Common/SprintEditorDialog/Resources";
import { IterationDropdown } from "Wit/Common/WorkItemControls/IterationDropdown";
import { compareIterations, normalizePath } from "Wit/Common/WorkItemControls/NodeUtils";
import { DayOfWeek, getIterationDateDefaultInformation, getNumberOfWorkingDays, getWeekendsFromWorkingDays } from "./IterationDateUtils";
import { validateEndDate, validateName, validateParentIterationValue, validateSelectedIteration, validateStartDate, WorkItemClassificationNodeWithParent } from "./SprintEditorFormValidators";

export interface ISprintEditorDialogProps {
    endDate?: Date;
    iterationGuid?: string;
    iterationName?: string;
    showTeamDropdown?: boolean;
    startDate?: Date;
    teamId?: string;
    teamName?: string;
    teamSettings?: IObservableValue<ITeamSettings | undefined>;
    onComplete?: () => void;
    onDismiss: () => void;
    navigateToSprintOnComplete?: (routeValues: { [key: string]: string }) => void;
}

interface INewSprintSuggestedDateInformation {
    /** The suggested start date, in UTC */
    suggestedStartDateUTC: Date;
    /** The number of working days in the previous iteration */
    workingDaysOffset: number;
}

const enum SprintDialogMode {
    CreateNewSprint,
    EditSprint,
    SelectExistingSprint
}

namespace SettingsConstants {
    export const SprintsHub = "Agile/SprintsHub";
    export const MruTeamId = "NewSprintTeamMru";
}

export class SprintEditorDialog extends VssComponent<ISprintEditorDialogProps> {
    private coreRestClient: ICoreRestClient;
    private createIterationError: IObservableValue<string> = new ObservableValue("");
    private isDirty: IObservableValue<boolean> = new ObservableValue(false);
    private isFormValid: IObservableValue<boolean> = new ObservableValue(false);
    private isLoading: IObservableValue<boolean> = new ObservableValue(true);
    private mode: IObservableValue<SprintDialogMode> = new ObservableValue(SprintDialogMode.CreateNewSprint);
    private projectId: string = "";
    private projectName: string = "";
    private witRestClient: IWorkItemTrackingRestClient;
    private workRestClient: IWorkRestClient;

    private defaultTeam: IObservableValue<WebApiTeamRef | undefined> = new ObservableValue(undefined);
    private selectedTeamBacklogIteration: IObservableValue<WorkItemClassificationNodeWithParent | undefined> = new ObservableValue(undefined);
    private selectedTeamIterationPaths: ObservableArray<string> = new ObservableArray();
    private teamId: IObservableValue<string> = new ObservableValue("");
    private teamName: IObservableValue<string> = new ObservableValue("");
    private teamSelection: DropdownSelection = new DropdownSelection();
    private teamSettings: IObservableValue<TeamSetting | undefined> = new ObservableValue(undefined);
    private teamsListBoxItems: ObservableArray<IListBoxItem> = new ObservableArray<IListBoxItem>();

    private name: IObservableValue<string> = new ObservableValue("");
    private nameValidationMessage: IObservableValue<string | undefined> = new ObservableValue(undefined);
    private nameValidationError: IObservableValue<boolean> = new ObservableValue(false);

    private startDate: IObservableValue<Date | undefined> = new ObservableValue(undefined);
    private startDateValidationError: IObservableValue<boolean> = new ObservableValue(false);
    private startDateValidationMessage: IObservableValue<string | undefined> = new ObservableValue(undefined);
    private suggestedDateInformation: IObservableValue<INewSprintSuggestedDateInformation | undefined> = new ObservableValue(undefined);
    private endDate: IObservableValue<Date | undefined> = new ObservableValue(undefined);
    private endDateValidationError: IObservableValue<boolean> = new ObservableValue(false);
    private endDateValidationMessage: IObservableValue<string | undefined> = new ObservableValue(undefined);

    private allNodesWithParents: ObservableArray<WorkItemClassificationNodeWithParent> = new ObservableArray();
    private iterationPath: IObservableValue<string> = new ObservableValue("");
    private iterationPathValidationMessage: IObservableValue<string | undefined> = new ObservableValue(undefined);
    private iterationPathValidationError: IObservableValue<boolean> = new ObservableValue(false);
    private location: IObservableValue<string> = new ObservableValue("");
    private locationValidationMessage: IObservableValue<string | undefined> = new ObservableValue(undefined);
    private locationValidationError: IObservableValue<boolean> = new ObservableValue(false);
    private selectedNode: IObservableValue<WorkItemClassificationNodeWithParent | undefined> = new ObservableValue(undefined);
    private selectedParentNode: IObservableValue<WorkItemClassificationNode | undefined> = new ObservableValue(undefined);

    private nameRef = React.createRef<TextField>();
    private teamsRef = React.createRef<EditableDropdown>();
    private iterationsRef = React.createRef<EditableDropdown>();

    constructor(props: ISprintEditorDialogProps, context: IVssComponentContext) {
        super(props, context);
        this.projectId = getTFSPageData(this.context.pageContext)?.project?.id ?? "";
        this.projectName = getTFSPageData(this.context.pageContext)?.project?.name ?? "";
        this.coreRestClient = context.pageContext.getRestClient<ICoreRestClient>("ICoreRestClient");
        this.witRestClient = getWorkItemTrackingClient(this.context.pageContext);
        this.workRestClient = getWorkClient(this.context.pageContext);
        this.loadData();
    }

    public componentDidUpdate(prevProps: ISprintEditorDialogProps) {
        super.componentDidUpdate(prevProps, {});
        this.loadData();
    }

    public render(): JSX.Element | null {
        return (
            <Observer mode={this.mode} isFormValid={this.isFormValid} isLoading={this.isLoading}>
                {(observerProps: { mode: SprintDialogMode, isFormValid: boolean, isLoading: boolean }) => {
                    return (
                        <Dialog
                            lightDismiss={false}
                            onDismiss={() => this.props.onDismiss()}
                            titleProps={{ iconProps: { iconName: "Sprint" }, text: observerProps.mode === SprintDialogMode.EditSprint ? Resources.EditSprint : Resources.NewSprint }}
                            footerButtonProps={[
                                {
                                    text: observerProps.mode === SprintDialogMode.EditSprint ? Resources.Save : Resources.Create,
                                    disabled: !observerProps.isFormValid || observerProps.isLoading,
                                    onClick: () => this.onCreate(),
                                    primary: true
                                },
                                {
                                    text: Resources.Cancel,
                                    onClick: () => this.props.onDismiss()
                                }
                            ]}
                        >
                            <Observer error={this.createIterationError}>
                                {(props: { error: string }) => {
                                    return (
                                        <>
                                            {props.error &&
                                                <MessageCard severity={MessageCardSeverity.Error} onDismiss={() => (this.createIterationError.value = "")} >
                                                    {props.error}
                                                </MessageCard>}
                                        </>
                                    );
                                }}
                            </Observer>
                            {this.props.showTeamDropdown &&
                                <FormItem className="padding-vertical-8" label={<FormLabelWithInfoIcon label={Resources.Team} tooltip={Resources.TeamInfoTooltip} />}>
                                    <Observer teamsListBoxItems={this.teamsListBoxItems}>
                                        {() =>
                                            <EditableDropdown
                                                allowFreeform={false}
                                                items={this.teamsListBoxItems}
                                                selection={this.teamSelection}
                                                ref={this.teamsRef}
                                                onValueChange={(item: IListBoxItem) => {
                                                    if (item.id !== this.teamId.value) {
                                                        this.isDirty.value = true;
                                                        this.teamSelection.select(this.teamsListBoxItems.value.findIndex(team => team.id === item.id));
                                                        this.teamId.value = item.id;
                                                        this.teamName.value = item.text ?? "";
                                                        const settingsService = this.context.pageContext.getService<ISettingsService>("ISettingsService");
                                                        const entries: { [key: string]: string } = {
                                                            [`${SettingsConstants.SprintsHub}/${SettingsConstants.MruTeamId}`]: item.id
                                                        };
                                                        settingsService.setEntries(entries, SettingsUserScope.Me);
                                                        this.updateInfoOnTeamChange();
                                                    }
                                                }}
                                            />
                                        }
                                    </Observer>
                                </FormItem>
                            }
                            {observerProps.mode !== SprintDialogMode.SelectExistingSprint ?
                                <>
                                    <FormItem
                                        className="padding-vertical-8"
                                        label={Resources.Name}
                                        error={this.nameValidationError}
                                        message={this.nameValidationMessage}
                                    >
                                        <TextField
                                            required={true}
                                            value={this.name}
                                            ref={this.nameRef}
                                            onChange={(event, value) => {
                                                this.validateAndUpdateNameFormItem(value);
                                            }}
                                        />
                                    </FormItem>
                                    <div className="flex-row padding-vertical-8">
                                        <FormItem
                                            className="start-date-picker full-size"
                                            label={Resources.Start}
                                            error={this.startDateValidationError}
                                            message={this.startDateValidationMessage}
                                        >
                                            <Observer startDate={this.startDate}>
                                                {(props: { startDate: Date }) => {
                                                    return (
                                                        <DatePicker
                                                            value={props.startDate}
                                                            onChange={date => {
                                                                this.validateAndUpdateDateFormItem(date, true);
                                                            }}
                                                        />
                                                    );
                                                }}
                                            </Observer>
                                        </FormItem>
                                        <FormItem
                                            className="full-size"
                                            label={Resources.End}
                                            error={this.endDateValidationError}
                                            message={this.endDateValidationMessage}
                                        >
                                            <Observer endDate={this.endDate}>
                                                {(props: { endDate: Date }) => {
                                                    return (
                                                        <DatePicker
                                                            value={props.endDate}
                                                            onChange={date => {
                                                                this.validateAndUpdateDateFormItem(date, false);
                                                            }}
                                                        />
                                                    );
                                                }}
                                            </Observer>
                                        </FormItem>
                                    </div>
                                    <FormItem
                                        className="padding-vertical-8"
                                        label={Resources.Location}
                                        error={this.locationValidationError}
                                        message={this.locationValidationMessage}
                                    >
                                        <Observer location={this.location}>
                                            {() => {
                                                return (
                                                    <IterationDropdown
                                                        allowFreeform={false}
                                                        disabled={observerProps.mode === SprintDialogMode.EditSprint}
                                                        onSetPath={this.onSetLocationPath}
                                                        selectedText={this.location}
                                                    />
                                                );
                                            }}
                                        </Observer>
                                    </FormItem>
                                </>
                                : <>
                                    <FormItem
                                        className="padding-vertical-8"
                                        label={<FormLabelWithInfoIcon label={Resources.Iteration} tooltip={Resources.IterationInfoTooltip} />}
                                        error={this.iterationPathValidationError}
                                        message={this.iterationPathValidationMessage}
                                    >
                                        <Observer iterationPath={this.iterationPath}>
                                            {() => {
                                                return (
                                                    <IterationDropdown
                                                        allowFreeform={false}
                                                        onSetPath={this.onSetIterationPath}
                                                        ref={this.iterationsRef}
                                                        selectedText={this.iterationPath}
                                                    />);
                                            }}
                                        </Observer>
                                    </FormItem>
                                </>
                            }
                            {observerProps.mode !== SprintDialogMode.EditSprint &&
                                <div className="flex-row padding-vertical-8">
                                    <div className="flex-grow flex-shrink rhythm-horizontal-4">
                                        {observerProps.mode === SprintDialogMode.SelectExistingSprint && this.getWorkingDaysMessage()}
                                    </div>
                                    <div className="rhythm-horizontal-4">
                                        <Button
                                            className="iteration-toggle-button"
                                            text={observerProps.mode === SprintDialogMode.CreateNewSprint ? Resources.SelectExistingIteration : Resources.CreateNewIteration}
                                            onClick={() => {
                                                this.mode.value = observerProps.mode === SprintDialogMode.CreateNewSprint ? SprintDialogMode.SelectExistingSprint : SprintDialogMode.CreateNewSprint;
                                                this.isFormValid.value = this.isCurrentFormValid();
                                                this.setFocusOnFirstElement();
                                            }}
                                        />
                                    </div>
                                </div>
                            }
                        </Dialog>
                    );
                }}
            </Observer>
        );
    }

    private async onCreate() {
        const teamContext = {
            project: this.projectName,
            projectId: this.projectId,
            team: this.teamName.value,
            teamId: this.teamId.value
        };
        if (this.isFormValid.value) {
            try {
                let iteration: any;
                if (this.mode.value === SprintDialogMode.EditSprint) {
                    if (this.isDirty.value) {
                        const patchNode = this.getPatchNode(this.name.value, this.startDate.value!, this.endDate.value!);
                        let iterationPath = normalizePath(this.allNodesWithParents.value.find(node => node.identifier === this.props.iterationGuid)?.path) ?? "";
                        iterationPath = iterationPath.substring(iterationPath.indexOf("\\") + 1);
                        const newNode = await this.witRestClient.updateClassificationNode(patchNode, this.projectId, TreeStructureGroup.Iterations, iterationPath);
                        if (newNode) {
                            this.isDirty.value = false;
                            this.props.onDismiss();
                        }
                    }
                    else {
                        this.props.onDismiss();
                    }
                }
                else {
                    if (this.mode.value === SprintDialogMode.CreateNewSprint) {
                        const patchNode = this.getPatchNode(this.name.value, this.startDate.value!, this.endDate.value!);
                        const parentPath = this.getRelativePath(this.selectedParentNode.value?.path ?? "");

                        const newNode = await this.witRestClient.createOrUpdateClassificationNode(patchNode, this.projectId, TreeStructureGroup.Iterations, parentPath);
                        if (newNode) {
                            iteration = { id: newNode.identifier };
                        }
                    } else {
                        iteration = { id: this.selectedNode.value?.identifier };
                    }
                    if (iteration && iteration.id) {
                        const postedIteration = await this.workRestClient.postTeamIteration(iteration, teamContext);
                        if (postedIteration) {
                            this.props.onDismiss();
                            if (this.props.onComplete) {
                                this.props.onComplete();
                            } else if (this.props.navigateToSprintOnComplete) {
                                const historyService = this.context.pageContext.getService<IVssHistoryService>("IVssHistoryService");
                                const routeValues: { [key: string]: string } = {};
                                routeValues["project"] = this.projectName;
                                routeValues["pivot"] = historyService.getState().pivot;
                                routeValues["teamName"] = this.teamName.value;
                                routeValues["iteration"] = postedIteration.path;
                                this.props.navigateToSprintOnComplete(routeValues);
                            }
                        }
                    }
                }
            }
            catch (error) {
                error = error as Error;
                this.createIterationError.value = error.message;
            }
        }
    }

    private async updateInfoOnTeamChange() {
        this.isLoading.value = true;
        await Promise.all([this.getSelectedTeamSettings(this.teamName.value, this.teamId.value), this.getTeamIterationPaths()]);
        this.computeSuggestedInformation();
        this.isLoading.value = false;
        this.validateAllInputs();
    }

    private validateAllInputs() {
        if (this.mode.value === SprintDialogMode.CreateNewSprint) {
            this.validateAndUpdateNameFormItem(this.name.value);
            this.validateAndUpdateDateFormItem(this.startDate.value, true);
            this.validateAndUpdateDateFormItem(this.endDate.value, false);
            this.onSetLocationPath(this.location.value);
        } else {
            this.onSetIterationPath(this.iterationPath.value);
        }
        this.isFormValid.value = this.isCurrentFormValid();
    }

    private validateAndUpdateNameFormItem(value: string) {
        this.name.value = value;
        if (this.selectedParentNode.value) {
            const nameValidationResult = validateName(value, this.selectedParentNode.value);
            this.nameValidationError.value = !!nameValidationResult;
            this.nameValidationMessage.value = nameValidationResult;
        }
        this.isDirty.value = true;
        this.isFormValid.value = this.isCurrentFormValid();
    }

    private validateAndUpdateDateFormItem(date: Date | undefined, isStartDate: boolean) {
        if (isStartDate) {
            this.startDate.value = date;
            const startDateValidationResult = validateStartDate(date);
            if (this.endDate.value) {
                const endDateValidationResult = validateEndDate(date, this.endDate.value);
                this.endDateValidationError.value = !!endDateValidationResult;
                this.endDateValidationMessage.value = endDateValidationResult;
            } else if (!startDateValidationResult && this.suggestedDateInformation.value) {
                const endDate = new Date(date!);
                endDate.setDate(endDate.getDate() + this.suggestedDateInformation.value.workingDaysOffset);
                const endDateValidationResult = validateEndDate(date, endDate);
                this.endDate.value = endDate;
                this.endDateValidationError.value = !!endDateValidationResult;
                this.endDateValidationMessage.value = endDateValidationResult;
            }
            this.startDateValidationError.value = !!startDateValidationResult;
            this.startDateValidationMessage.value = startDateValidationResult;
        } else {
            this.endDate.value = date;
            const endDateValidationResult = validateEndDate(this.startDate.value, date);
            this.endDateValidationError.value = !!endDateValidationResult;
            this.endDateValidationMessage.value = endDateValidationResult;
        }
        this.isDirty.value = true;
        this.isFormValid.value = this.isCurrentFormValid();
    }

    private isCurrentFormValid(): boolean {
        if (
            (this.mode.value !== SprintDialogMode.SelectExistingSprint &&
                this.name.value &&
                !this.nameValidationError.value &&
                this.startDate.value &&
                !this.startDateValidationError.value &&
                this.endDate.value &&
                !this.endDateValidationError.value &&
                this.location.value &&
                !this.locationValidationError.value) ||
            (this.mode.value === SprintDialogMode.SelectExistingSprint &&
                this.iterationPath.value &&
                !this.iterationPathValidationError.value)
        ) {
            return true;
        }
        return false;
    }

    private onSetLocationPath = (path: string) => {
        this.location.value = path;
        this.selectedParentNode.value = this.allNodesWithParents.value.find(node => normalizePath(node.path) === path);
        const locationValidationResult = validateParentIterationValue(path, this.selectedParentNode.value, this.selectedTeamBacklogIteration.value);
        this.locationValidationError.value = !!locationValidationResult;
        this.locationValidationMessage.value = locationValidationResult;
        if (!locationValidationResult) {
            this.validateAndUpdateNameFormItem(this.name.value);
        }
        this.isDirty.value = true;
        this.isFormValid.value = this.isCurrentFormValid();
    }

    private onSetIterationPath = (path: string) => {
        this.iterationPath.value = path;
        this.selectedNode.value = this.allNodesWithParents.value.find(node => normalizePath(node.path) === path);
        const iterationPathValidationResult = validateSelectedIteration(path, this.selectedTeamIterationPaths.value, this.selectedNode.value, this.selectedTeamBacklogIteration.value);
        this.iterationPathValidationError.value = !!iterationPathValidationResult;
        this.iterationPathValidationMessage.value = iterationPathValidationResult;
        this.isDirty.value = true;
        this.isFormValid.value = this.isCurrentFormValid();
    }

    private getWorkingDaysMessage = (): string => {
        let daysOff: DayOfWeek[] = [];
        let startDate: Date = new Date();
        let endDate: Date = new Date();
        let message = "";
        if (this.props.showTeamDropdown && this.teamSettings.value) {
            daysOff = getWeekendsFromWorkingDays(this.teamSettings.value.workingDays);
        } else if (!this.props.showTeamDropdown && this.props.teamSettings && this.props.teamSettings.value) {
            daysOff = this.props.teamSettings.value.weekends.days;
        }

        if (this.selectedNode.value) {
            if (this.selectedNode.value.attributes && this.selectedNode.value.attributes["startDate"] && this.selectedNode.value.attributes["finishDate"]) {
                startDate = shiftToUTC(this.selectedNode.value.attributes["startDate"]);
                endDate = shiftToUTC(this.selectedNode.value.attributes["finishDate"]);
                const workingDays = getNumberOfWorkingDays(startDate, endDate, daysOff);
                message = format(Resources.SelectedIterationWorkingDaysText, workingDays);
            } else {
                message = Resources.SelectedIterationUnsetWorkingDaysText;
            }
        }
        return message;
    }

    private async getSelectedTeamSettings(teamName: string, teamId: string) {
        const teamContext = {
            project: this.projectName,
            projectId: this.projectId,
            team: teamName,
            teamId: teamId
        };
        this.teamSettings.value = await this.workRestClient.getTeamSettings(teamContext);
    }

    private async loadData() {
        if (this.props.showTeamDropdown) {
            const teamService = this.context.pageContext.getService<ITfsTeamService>("ITfsTeamService");
            const teams: WebApiTeam[] = await teamService.getAllTeams(this.projectId);
            const teamsData: IListBoxItem[] = [];
            if (teams && teams.length > 0) {
                teams.forEach(team => {
                    teamsData.push({
                        id: team.id,
                        text: team.name
                    });
                });
                const projectData = await this.coreRestClient.getProject(this.projectId);
                if (projectData && projectData.defaultTeam) {
                    this.defaultTeam.value = projectData.defaultTeam;
                }
                this.teamsListBoxItems.value = teamsData;
                const settingsService = this.context.pageContext.getService<ISettingsService>("ISettingsService");
                const mruTeamId = settingsService.getEntry(`${SettingsConstants.SprintsHub}/${SettingsConstants.MruTeamId}`, SettingsUserScope.Me);
                let selectedTeamIndex = -1;
                if (mruTeamId) {
                    selectedTeamIndex = teamsData.findIndex(team => team.id === mruTeamId);
                }
                if (selectedTeamIndex === -1) {
                    selectedTeamIndex = teamsData.findIndex(team => team.id === projectData.defaultTeam.id);
                }
                if (selectedTeamIndex !== -1) {
                    this.teamId.value = teamsData[selectedTeamIndex].id;
                    this.teamName.value = teamsData[selectedTeamIndex].text ?? "";
                    this.teamSelection.select(selectedTeamIndex);
                    this.getSelectedTeamSettings(this.teamName.value, this.defaultTeam.value?.id ?? "");
                }
            }
        } else {
            if (this.props.teamSettings && this.props.teamSettings.value) {
                this.teamId.value = this.props.teamSettings.value.teamId ?? "";
                this.teamName.value = this.props.teamSettings.value.teamName ?? "";
            }
            else if (this.props.iterationGuid && this.props.iterationName && this.props.teamId && this.props.teamName) {
                this.mode.value = SprintDialogMode.EditSprint;
                this.name.value = this.props.iterationName;
                this.teamId.value = this.props.teamId;
                this.teamName.value = this.props.teamName;
                this.startDate.value = this.props.startDate ? shiftToUTC(this.props.startDate) : undefined;
                this.endDate.value = this.props.endDate ? shiftToUTC(this.props.endDate) : undefined;
            }
        }
        await Promise.all([this.getAllIterationNodes(), this.getTeamIterationPaths()]);
        if (this.mode.value !== SprintDialogMode.EditSprint) {
            this.computeSuggestedInformation();
        }
        else {
            this.selectedParentNode.value = this.allNodesWithParents.value.find(node => node.identifier === this.props.iterationGuid)?.parent;
            this.location.value = normalizePath(this.selectedParentNode.value?.path);
        }
        this.isLoading.value = false;
        this.isFormValid.value = this.isCurrentFormValid();

        this.setFocusOnFirstElement();
    }

    private setFocusOnFirstElement() {
        if (this.props.showTeamDropdown ) {
            if (this.teamsRef) {
                this.teamsRef.current?.focus();
                return;
            }
        }

        switch (this.mode.value) {  
            case SprintDialogMode.CreateNewSprint:
            case SprintDialogMode.EditSprint:
                if (this.nameRef) {
                    this.nameRef.current?.focus();
                }
                break;
            case SprintDialogMode.SelectExistingSprint:
                if (this.iterationsRef) {
                    this.iterationsRef.current?.focus();
                }
                break;
        }      
    }

    private async getAllIterationNodes() {
        const rootNodes = await this.witRestClient.getRootNodes(this.projectId, 15);
        const iterationRootNode = rootNodes.find(n => n.structureType === TreeNodeStructureType.Iteration);
        if (iterationRootNode) {
            const nodesWithParents = this.convertToNodesWithParentPointers(iterationRootNode);
            this.allNodesWithParents.value = nodesWithParents;
        }
    }

    private async getTeamIterationPaths() {
        const teamIterations = await this.workRestClient.getTeamIterations({ projectId: this.projectId, teamId: this.teamId.value } as TeamContext);
        if (teamIterations) {
            const iterationPathStrings = teamIterations.map(itPath => itPath.path.split('/').join('\\'));
            this.selectedTeamIterationPaths.value = iterationPathStrings;
        }
    }

    private computeSuggestedInformation() {
        this.tryGetNextSuggestedIterationPath(this.selectedTeamIterationPaths.value, this.allNodesWithParents.value);
        if (this.iterationPath.value) {
            this.mode.value = SprintDialogMode.SelectExistingSprint;
            this.selectedNode.value = this.allNodesWithParents.value.find(node => normalizePath(node.path) === this.iterationPath.value);
        } else {
            this.mode.value = SprintDialogMode.CreateNewSprint;
        }
        if (!this.selectedParentNode.value) {
            // select the project's root iteration node by default if no suggested parent is found
            this.selectedParentNode.value = this.allNodesWithParents.value[0];
            this.location.value = normalizePath(this.selectedParentNode.value.path);
        }
        let selectedTeamDaysOff: DayOfWeek[] = [];
        if (this.props.showTeamDropdown && this.teamSettings.value && this.teamSettings.value.workingDays) {
            selectedTeamDaysOff = getWeekendsFromWorkingDays(this.teamSettings.value.workingDays);
            this.selectedTeamBacklogIteration.value = this.allNodesWithParents.value.find(node => node.identifier === this.teamSettings.value?.backlogIteration.id);
        } else if (this.props.teamSettings && this.props.teamSettings.value && this.props.teamSettings.value.weekends.days) {
            selectedTeamDaysOff = this.props.teamSettings.value.weekends.days;
            this.selectedTeamBacklogIteration.value = this.allNodesWithParents.value.find(node => node.identifier === this.props.teamSettings?.value?.backlogIteration.id);
        }
        if (selectedTeamDaysOff.length > 0 && this.selectedParentNode.value) {
            this.suggestedDateInformation.value = this.computeSuggestedDateInformation(this.selectedParentNode.value, selectedTeamDaysOff);
            if (this.suggestedDateInformation.value) {
                this.suggestedDateInformation.value = this.suggestedDateInformation.value;
                this.startDate.value = this.suggestedDateInformation.value.suggestedStartDateUTC;
                const endDate: Date = new Date(this.suggestedDateInformation.value.suggestedStartDateUTC);
                endDate.setDate(endDate.getDate() + this.suggestedDateInformation.value.workingDaysOffset);
                this.endDate.value = endDate;
            }
        }
    }

    private tryGetNextSuggestedIterationPath(iterationPaths: string[], nodesWithParents: WorkItemClassificationNodeWithParent[]) {
        let node: WorkItemClassificationNodeWithParent | undefined;
        let parentNode: WorkItemClassificationNodeWithParent | undefined;
        if (!iterationPaths || iterationPaths.length === 0) {
            this.iterationPath.value = "";
        } else {
            const lastSelectedPath = iterationPaths[iterationPaths.length - 1];
            node = nodesWithParents.find(node => normalizePath(node.path) === lastSelectedPath);
            parentNode = node?.parent;
            if (node && parentNode) {
                const siblings = parentNode.children;
                siblings.sort(compareIterations);
                if (siblings) {
                    const index = siblings.map(sibling => sibling.id).indexOf(node.id);
                    this.iterationPath.value = (index >= 0 && index < siblings.length - 1) ?
                        this.replaceLeafPath(lastSelectedPath, siblings[index + 1].name) : "";
                }
                this.selectedParentNode.value = node.parent;
                this.location.value = normalizePath(this.selectedParentNode.value?.path);
            } else {
                this.iterationPath.value = "";
            }
        }
    }

    private replaceLeafPath(pathToBeReplaced: string, leafPathName: string): string {
        const pathSegments = pathToBeReplaced.split("\\");
        pathSegments[pathSegments.length - 1] = leafPathName;
        return pathSegments.join("\\");
    }

    private computeSuggestedDateInformation(parentNode: WorkItemClassificationNodeWithParent, weekends: DayOfWeek[]): INewSprintSuggestedDateInformation | undefined {
        if (parentNode && parentNode.children && parentNode.children.length > 0) {
            const lastSibling = this.findLastSiblingWithDates(parentNode);
            if (lastSibling && lastSibling.attributes) {
                const {
                    suggestedStartDate,
                    workingDaysOffset
                } = getIterationDateDefaultInformation(lastSibling.attributes["startDate"], lastSibling.attributes["finishDate"], weekends);
                return {
                    suggestedStartDateUTC: shiftToUTC(suggestedStartDate),
                    workingDaysOffset: workingDaysOffset
                };
            }
        }
        return undefined;
    }

    private findLastSiblingWithDates(parentNode: WorkItemClassificationNodeWithParent): WorkItemClassificationNodeWithParent | undefined {
        // Find the last node with dates
        let lastChild: WorkItemClassificationNodeWithParent | undefined;
        for (let i = parentNode.children.length - 1; i >= 0; i--) {
            const child = parentNode.children[i];
            if (child.attributes && child.attributes["startDate"] && child.attributes["startDate"]) {
                lastChild = child;
                break;
            }
        }

        return lastChild;
    }

    private convertToNodesWithParentPointers(node: WorkItemClassificationNode, parent?: WorkItemClassificationNodeWithParent): WorkItemClassificationNodeWithParent[] {
        const item: WorkItemClassificationNodeWithParent = { ...node, parent: parent };
        const items = [item];
        if (node.hasChildren) {
            const children = [...node.children].sort(compareIterations);
            for (const child of children) {
                items.push(...this.convertToNodesWithParentPointers(child, item));
            }
        }
        return items;
    }

    private getPatchNode(iterationName: string, iterationStartDate: Date, iterationEndDate: Date): any {
        return {
            name: iterationName,
            attributes: {
                startDate: new Date(Date.UTC(iterationStartDate.getFullYear(), iterationStartDate.getMonth(), iterationStartDate.getDate())),
                finishDate: new Date(Date.UTC(iterationEndDate.getFullYear(), iterationEndDate.getMonth(), iterationEndDate.getDate()))
            }
        };
    }

    private getRelativePath(path: string): string {
        return path.split("\\").filter(x => x).slice(2).join("\\");
    }
}

function FormLabelWithInfoIcon({ label, tooltip }: { label: string; tooltip: string }) {
    return (
        <div className="flex-row rhythm-horizontal-4">
            {label}
            <div className="padding-left-4">
                <Button
                    className="info-button"
                    ariaLabel={tooltip}
                    iconProps={{ iconName: "Info", size: IconSize.small }}
                />
            </div>
        </div>
    );
}